import dialogStyles from '@/components/ui/Dialog/Dialog.module.css';
import Dialog from '@/components/ui/Dialog/Dialog';

export type InfoDialogType =
  | 'VECTOR_ONLY'
  | 'HYBRID'
  | 'RAG_ENHANCED'
  | 'STRICTNESS'
  | 'DEPTH'
  | 'CONFIDENCE'
  | null;

const renderDialogContent = (type: InfoDialogType) => {
  switch (type) {
    case 'VECTOR_ONLY':
      return (
        <div className={dialogStyles.dialogBodyWrapper}>
          <div className={dialogStyles.dialogContent}>
            <p>
              <strong>Quick Scan</strong> focuses on rapid pattern matching
              between documents. It uses mathematical similarity to
              automatically categorize findings.
            </p>
            <table className={dialogStyles.infoTable}>
              <thead>
                <tr>
                  <th>Matching Strength</th>
                  <th>Categorization</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>&ge; 85%</td>
                  <td>
                    <span className={dialogStyles.tagGreen}>ALIGNMENT</span>{' '}
                    (Fully covered)
                  </td>
                </tr>
                <tr>
                  <td>70% - 85%</td>
                  <td>
                    <span className={dialogStyles.tagBlue}>SUPPLEMENT</span>{' '}
                    (Partially covered)
                  </td>
                </tr>
                <tr>
                  <td>50% - 70%</td>
                  <td>
                    <span className={dialogStyles.tagYellow}>GAP</span>{' '}
                    (Potential issue)
                  </td>
                </tr>
                <tr>
                  <td>&lt; 50%</td>
                  <td>
                    <span className={dialogStyles.tagRed}>GAP</span> (High Risk
                    / Missing)
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      );
    case 'HYBRID':
      return (
        <div className={dialogStyles.dialogBodyWrapper}>
          <div className={dialogStyles.dialogContent}>
            <p>
              <strong>Smart Analysis</strong> is a balanced approach designed
              for complex regulatory reviews.
            </p>
            <ul>
              <li>
                <strong>Initial Screening</strong>: The system maps all document
                overlaps mathematically.
              </li>
              <li>
                <strong>AI Audit</strong>: For critical gaps or ambiguous
                sections, the AI steps in to verify if a requirement is actually
                met or missing.
              </li>
            </ul>
          </div>
        </div>
      );
    case 'RAG_ENHANCED':
      return (
        <div className={dialogStyles.dialogBodyWrapper}>
          <div className={dialogStyles.dialogContent}>
            <p>
              <strong>Deep Dive</strong> provides a full AI-driven audit of your
              documents.
            </p>
            <p>
              Every potential overlap is analyzed directly by the AI, regardless
              of its mathematical similarity. This provides the highest level of
              detail and provides conversational explanations for every gap
              found.
            </p>
          </div>
        </div>
      );
    case 'STRICTNESS':
      return (
        <div className={dialogStyles.dialogBodyWrapper}>
          <div className={dialogStyles.dialogContent}>
            <p>
              <strong>Match Strictness</strong> controls the "sensitivity" of
              the initial search.
            </p>
            <ul>
              <li>
                <strong>Strict</strong>: Only finds sections that use nearly
                identical wording.
              </li>
              <li>
                <strong>Loose</strong>: Finds sections that discuss the same
                topics even if the language used is very different.
              </li>
            </ul>
          </div>
        </div>
      );
    case 'DEPTH':
      return (
        <div className={dialogStyles.dialogBodyWrapper}>
          <div className={dialogStyles.dialogContent}>
            <p>
              <strong>Search Depth</strong> determines how many potential
              matching sections from your internal document are retrieved for
              every single regulatory section.
            </p>
            <p>
              Higher depth ensures that even if a regulation is addressed across
              multiple pages in your internal documents, the AI will find and
              group all of them.
            </p>
          </div>
        </div>
      );
    case 'CONFIDENCE':
      return (
        <div className={dialogStyles.dialogBodyWrapper}>
          <div className={dialogStyles.dialogContent}>
            <p>
              <strong>AI Confidence Threshold</strong> filters out findings
              where the AI is uncertain about its analysis.
            </p>
            <ul>
              <li>
                <strong>Higher values</strong>: Only show findings the AI is
                very confident about (fewer false positives).
              </li>
              <li>
                <strong>Lower values</strong>: Include more findings, but may
                contain less certain matches (broader coverage).
              </li>
            </ul>
          </div>
        </div>
      );
    default:
      return null;
  }
};

const renderDialogHeader = (type: InfoDialogType) => {
  const titles: Record<string, { icon: string; label: string }> = {
    VECTOR_ONLY: { icon: 'pi-bolt', label: 'Quick Scan' },
    HYBRID: { icon: 'pi-sitemap', label: 'Smart Analysis' },
    RAG_ENHANCED: { icon: 'pi-star-fill', label: 'Deep Dive' },
    STRICTNESS: { icon: 'pi-crosshairs', label: 'Match Strictness' },
    DEPTH: { icon: 'pi-search', label: 'Search Depth' },
    CONFIDENCE: { icon: 'pi-shield', label: 'AI Confidence Threshold' }
  };
  const title = type ? titles[type] : undefined;
  return title ? (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        color: 'white'
      }}
    >
      <i className={`pi ${title.icon}`} />
      <span>{title.label}</span>
    </div>
  ) : (
    'Technical Details'
  );
};

interface InfoDialogProps {
  visible: boolean;
  type: InfoDialogType;
  onHide: () => void;
}

export const InfoDialog = ({ visible, type, onHide }: InfoDialogProps) => {
  return (
    <Dialog
      header={renderDialogHeader(type)}
      visible={visible}
      style={{ width: '90vw', maxWidth: '700px' }}
      onHide={onHide}
      className={dialogStyles.customDialog}
      draggable={false}
      modal
      maximizable={false}
      contentStyle={{ padding: '0px' }}
      headerStyle={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}
    >
      {renderDialogContent(type)}
    </Dialog>
  );
};
